<script lang="ts" setup>
import { flattenEntity } from 'strapi-flatten-graphql'

const { $config } = useNuxtApp()
const route = useRoute()
const modalOpen = ref(false)
const isLoading = ref(true)
const article = ref()
const relatedArticle = ref()
const authors = ref()
const clickedTOC = ref()
const strapiConfig = useStrapiAPIConfig()
const excludeTOC = ['article.job-slider', 'article.company-slider', 'article.employer-section']
const showEmployer = ref(false)

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

try {
  const [articleData, authorsData] = await Promise.all([
    useAsyncData('get_articles', () => strapiConfig(`/articles/${route.params.id}?populate[meta]=*&populate[contents][populate][image]=*&populate[topics]=*&populate[thumbnail]=*&populate[file_attachment]=*&populate[createdBy]=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`), { server: true }),
    useAsyncData('get_authors', () => strapiConfig(`/authors?populate=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`), { server: true }),
  ])
  const rawArticle = articleData.data.value
  if (!isEmpty(rawArticle)) {
    const flattenResp = flattenEntity(rawArticle.data)
    if (flattenResp.locale.toLowerCase() === useStrapiLocale(useGetSubdomain(), null).toLowerCase()) {
      article.value = {
        ...flattenResp,
        createdBy: !isEmpty(flattenResp?.createdBy?.data) ? flattenEntity(flattenResp?.createdBy?.data) : '',
        file_attachment: !isEmpty(flattenResp?.file_attachment?.data) ? flattenEntity(flattenResp?.file_attachment?.data) : [],
        thumbnail: !isEmpty(flattenResp?.thumbnail?.data) ? flattenEntity(flattenResp?.thumbnail?.data) : [],
        topics: flattenResp.topics?.data.map((topic: any) => flattenEntity(topic)),
      }
      showEmployer.value = article.value.topics.some((topic: any) => topic.show_employer === true)
      setMeta(flattenResp?.meta, flattenResp.title, flattenResp.summary)

      // Fetch related articles
      const queryString = article.value.topics.map(item => `filters[topics][title][$eq]=${encodeURIComponent(item.title)}`).join('&')
      const relatedData = await useAsyncData('get_related', () => strapiConfig(`/articles?${queryString}&filters[id][$ne]=${article.value.id}&sort=createdAt:desc&pagination[pageSize]=6&populate=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`), { server: true })
      const rawRelated = relatedData.data.value
      if (!isEmpty(rawRelated?.data)) {
        relatedArticle.value = rawRelated.data.map((item: any) => {
          const article = flattenEntity(item)
          const thumbnail = article.thumbnail?.data ? flattenEntity(article.thumbnail?.data) : null
          const topics = article.topics?.data.map((topic: any) => flattenEntity(topic))
          const createdBy = article.createdBy?.data ? flattenEntity(article.createdBy?.data) : null
          const fileAttachment = article.file_attachment?.data ? flattenEntity(article.file_attachment?.data) : null
          return { ...article, thumbnail, topics, createdBy, file_attachment: fileAttachment }
        })
      }

      const rawAuthors = authorsData.data.value
      if (!isEmpty(rawAuthors)) {
        authors.value = rawAuthors.data.map((item: any) => {
          const author = flattenEntity(item)
          const thumbnail = !isEmpty(author.thumbnail) ? author.thumbnail?.data.map((topic: any) => flattenEntity(topic))[0] : []
          return { ...author, thumbnail }
        })
      }
    }
    else {
      showError({ message: 'Page not found', statusCode: 404 })
    }
  }
  else {
    showError({ message: 'Page not found', statusCode: 404 })
  }
}
catch (error) {
  console.error('Error fetching data', error)
  showError({ message: 'Failed to load data', statusCode: 500 })
}
finally {
  isLoading.value = false
}

function selectAuthor(username: string) {
  return authors.value.find((author: any) => author.username === username)
}

function setMeta(metaData: any, titleArticle: string, descArticle: string) {
  const title = metaData?.title || titleArticle || ''
  const description = metaData?.description || descArticle || ''
  const keywords = metaData?.keywords || ''
  const robots = ($config.public.ENV === 'development' || $config.public.ENV === 'staging') ? 'nofollow, noindex' : (metaData?.robots || 'follow, index')
  const canonical = metaData?.canonical || useSetSubdomain(useGetSubdomain()) + route.path || ''
  const seoImage = article.value.thumbnail?.url || 'https://files.epicareer.com/assets/images/share.png'

  useHead({
    title,
    meta: [
      { property: 'og:title', content: title },
      { name: 'twitter:title', content: title },
      { name: 'description', content: description },
      { property: 'og:description', content: description },
      { name: 'twitter:description', content: description },
      { name: 'keywords', content: keywords || null },
      { property: 'og:url', content: route.fullPath },
      { name: 'twitter:url', content: route.fullPath },
      { property: 'og:type', content: 'article' },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { property: 'og:image', content: seoImage },
      { name: 'twitter:image', content: seoImage },
      { property: 'og:image:secure_url', content: seoImage },
      { name: 'twitter:image:src', content: seoImage },
      { name: 'robots', content: robots },
    ],
    link: [
      { rel: 'canonical', href: canonical },
    ],
  })
}
</script>

<template>
  <BaseAtomsSkeletonBounded v-if="!isLoading" container="full" class="max-w-6xl !xl:px-0 !lg:pt-10 mx-auto text-gray-900">
    <ContinueFormModal :open="modalOpen" :redirect-val="route.fullPath" @open-modal="(item: boolean) => modalOpen = item" />
    <div class="<lg:px-4 <lg:pt-6">
      <Icon name="heroicons:arrow-left-solid" class="h-6 w-6 cursor-pointer mb-4 md:mb-6" @click="$router.back()" />
      <!-- breadcrumb -->
      <div class="flex items-center mb-4 text-xs line-clamp-1">
        <NuxtLink to="/career-guide" class="text-[#5843E5]">
          Career Guide
        </NuxtLink>
        <Icon name="material-symbols:chevron-right" class="w-3 h-3 mx-2" />
        <span>
          {{ article.title }}
        </span>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-x-6">
      <div class="col-spans-12 lg:col-spans-8 gap-y-4 grid auto-rows-min">
        <div class="bg-white py-6 px-4 lg:px-10 gap-y-4 grid">
          <!-- title section -->
          <div class="grid gap-y-2">
            <p class="text-xs text-gray-600">
              {{ getArticleReadingTime(article?.title, article?.summary, article?.contents) }}
            </p>
            <h1 class="text-3xl font-bold">
              {{ article.title }}
            </h1>
            <p class="leading-6 text-gray-600">
              {{ article.summary }}
            </p>
          </div>
          <!-- author section -->
          <div class="flex items-center">
            <NuxtLink :to="`/career-guide/author/${useTextToParam(selectAuthor(article?.createdBy?.username)?.name, false)?.toLowerCase()}`" class="flex-shrink-0 flex items-center mr-4">
              <img :src="selectAuthor(article?.createdBy?.username)?.thumbnail?.url" class="w-8 h-8 object-cover rounded-full">
            </NuxtLink>
            <div class="text-xs">
              <NuxtLink :to="`/career-guide/author/${useTextToParam(selectAuthor(article?.createdBy?.username)?.name, false)?.toLowerCase()}`" class="text-sm text-[#5843E5] font-semibold">
                {{ selectAuthor(article?.createdBy?.username)?.name }}
              </NuxtLink>
              <p class="text-gray-500 capitalize">
                Updated {{ getArticleFormatDate(article?.publishedAt) }}
              </p>
            </div>
          </div>
          <!-- thumbnail section -->
          <div v-if="article?.thumbnail?.url">
            <img v-if="article?.thumbnail?.url" :src="article?.thumbnail?.url" class="aspect-video object-cover w-full">
            <span v-if="article?.thumbnail_description" class="text-[10px] text-gray-500 mt-1">{{ article?.thumbnail_description }}</span>
          </div>
          <!-- component section -->
          <div class="grid gap-y-6">
            <div v-for="(item, idx) in article?.contents" :key="idx">
              <component :is="useStrapiComponent(item.__component)" :id="useStrapiComponent(item.__component) + item.id" :datas="item" />
            </div>
          </div>
          <!-- author section -->
          <div class="bg-[#EFF6FF] rounded-2xl py-4 px-6">
            <div class="flex items-center mb-4">
              <NuxtLink :to="`/career-guide/author/${useTextToParam(selectAuthor(article?.createdBy?.username)?.name, false)?.toLowerCase()}`" class="flex-shrink-0 flex items-center mr-4">
                <img :src="selectAuthor(article?.createdBy?.username)?.thumbnail?.url" class="w-16 h-16 object-cover rounded-full">
              </NuxtLink>
              <div>
                <NuxtLink :to="`/career-guide/author/${useTextToParam(selectAuthor(article?.createdBy?.username)?.name, false)?.toLowerCase()}`" class="font-semibold hover:underline">
                  {{ selectAuthor(article?.createdBy?.username)?.name }}
                </NuxtLink>
                <p class="text-gray-600 text-xs">
                  {{ selectAuthor(article?.createdBy?.username)?.position }}
                </p>
              </div>
            </div>
            <div class="text-xs">
              {{ selectAuthor(article?.createdBy?.username)?.description }}
            </div>
          </div>
          <!-- tags section -->
          <div class="my-2">
            <p class="text-xs font-semibold mb-4">
              Topic tags
            </p>
            <div class="space-x-2 leading-8">
              <NuxtLink v-for="(topic, idx) in article?.topics" :key="idx" :to="`/career-guide?topic=${useTextToParam(topic.title.toLowerCase(), false)}&file=false&page=1`" class="bg-gray-100 hover:bg-[#5843e5] hover:text-white rounded-full py-1 px-2 text-xs">
                {{ topic?.title }}
              </NuxtLink>
            </div>
          </div>
        </div>
        <!-- downloadables mobile -->
        <div v-if="!isEmpty(article?.file_attachment)" class="block lg:hidden px-4">
          <CareerGuideDownload :article="article" @modal-open="modalOpen = true" />
        </div>
        <!-- employer mobile -->
        <div v-if="showEmployer" class="block lg:hidden px-4">
          <CareerGuideMiniEmployerSection />
        </div>
        <!-- sharing mobile -->
        <div class="block lg:hidden px-4">
          <CareerGuideSharing :url-path="route.path" :title="article.title" />
        </div>
        <!-- related articles -->
        <div v-if="!isEmpty(relatedArticle)" class="<lg:px-4">
          <p class="text-2xl font-bold mb-6">
            Related Articles
          </p>
          <div class="grid lg:grid-cols-2 gap-6">
            <CareerGuideArticleCard v-for="(item, idx) in relatedArticle" :key="idx" :is-loading="false" :article="item" :author="selectAuthor(item?.createdBy?.username)" :compact-mode="true" />
          </div>
        </div>
      </div>
      <div class="grid auto-rows-min col-spans-12 lg:col-spans-4">
        <div class="sticky top-20 grid gap-y-6">
          <!-- TOC -->
          <div v-if="!isEmpty(article.contents)" class="bg-white rounded-xl p-6 hidden lg:block">
            <p class="text-lg font-bold mb-2">
              Table of Contents
            </p>
            <div class="h-70 overflow-y-scroll">
              <div v-for="(item, idx) in article.contents" :key="idx">
                <p v-if="item?.title && !excludeTOC.includes(item.__component)" :class="`${clickedTOC === `${useStrapiComponent(item.__component)}${item.id}` ? 'text-gray-900 font-bold' : 'text-gray-400'} grid gap-y-4 mt-4 cursor-pointer`" @click="clickedTOC = `${useStrapiComponent(item.__component)}${item.id}`; useScrollTo(`${useStrapiComponent(item.__component)}${item.id}`)">
                  {{ item?.title }}
                </p>
              </div>
            </div>
          </div>
          <!-- downloadables desktop -->
          <div v-if="!isEmpty(article?.file_attachment)" class="hidden lg:block">
            <CareerGuideDownload :article="article" @modal-open="modalOpen = true" />
          </div>
          <!-- employer desktop -->
          <div v-if="showEmployer" class="hidden lg:block">
            <CareerGuideMiniEmployerSection />
          </div>
          <!-- sharing desktop -->
          <div class="hidden lg:block">
            <CareerGuideSharing :url-path="route.path" :title="article.title" />
          </div>
        </div>
      </div>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
